/**
 * Lindy Product Details Tabs Component
 * Custom Web Component for managing tab/accordion behavior
 */

class ProductTabsComponent extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.product-tabs__tab');
    this.panels = this.querySelectorAll('.product-tabs__panel');
    this.isMobile = window.innerWidth < 750;
  }

  connectedCallback() {
    this.initTabs();
    this.handleResponsive();
    window.addEventListener('resize', () => this.handleResponsive());
  }

  initTabs() {
    // Desktop tab navigation
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const targetPanel = tab.dataset.tab;
        this.switchTab(targetPanel);
      });
    });

    // Mobile accordion behavior
    this.panels.forEach((panel) => {
      const header = this.createMobileHeader(panel);
      panel.insertBefore(header, panel.firstChild);

      header.addEventListener('click', (e) => {
        e.preventDefault();
        this.togglePanel(panel);
      });
    });
  }

  createMobileHeader(panel) {
    const header = document.createElement('div');
    header.className = 'product-tabs__mobile-title';

    const titleId = panel.getAttribute('aria-labelledby');
    const tab = this.querySelector(`#${titleId}`);
    const titleText = tab ? tab.textContent : '';

    const title = document.createElement('span');
    title.textContent = titleText;

    const icon = document.createElement('div');
    icon.className = 'product-tabs__mobile-icon';

    header.appendChild(title);
    header.appendChild(icon);

    return header;
  }

  switchTab(targetPanel) {
    // Remove active class from all tabs and panels
    this.tabs.forEach((tab) => {
      tab.classList.remove('active');
      tab.setAttribute('aria-selected', 'false');
    });

    this.panels.forEach((panel) => {
      panel.classList.remove('active');
    });

    // Add active class to target tab and panel
    const activeTab = this.querySelector(`[data-tab="${targetPanel}"]`);
    const activePanel = this.querySelector(`[data-panel="${targetPanel}"]`);

    if (activeTab && activePanel) {
      activeTab.classList.add('active');
      activeTab.setAttribute('aria-selected', 'true');
      activePanel.classList.add('active');
    }
  }

  togglePanel(panel) {
    const wasClosed = !panel.classList.contains('active');

    if (this.isMobile) {
      // On mobile, close all other panels
      this.panels.forEach((p) => {
        if (p !== panel) {
          p.classList.remove('active');
        }
      });
    }

    // Toggle current panel
    if (wasClosed) {
      panel.classList.add('active');
    } else {
      panel.classList.remove('active');
    }
  }

  handleResponsive() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 750;

    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        // On mobile, close all panels by default except first
        this.panels.forEach((panel, index) => {
          if (index === 0) {
            panel.classList.add('active');
          } else {
            panel.classList.remove('active');
          }
        });
      } else {
        // On desktop, show the active tab panel
        const activeTab = this.querySelector('.product-tabs__tab.active');
        if (activeTab) {
          const targetPanel = activeTab.dataset.tab;
          this.switchTab(targetPanel);
        } else if (this.tabs.length > 0) {
          // If no active tab, activate first one
          const firstPanel = this.tabs[0].dataset.tab;
          this.switchTab(firstPanel);
        }
      }
    }
  }
}

customElements.define('product-tabs-component', ProductTabsComponent);
